import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const SendAnnouncementModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'normal',
    recipients: []
  });
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTeamsAndUsers();
    }
  }, [isOpen]);

  const fetchTeamsAndUsers = async () => {
    try {
      // Fetch teams - FIXED: Changed 'name' to 'team_name' to match database schema
      const { data: teamsData, error: teamsError } = await supabase
        ?.from('teams')
        ?.select('id, team_name')
        ?.order('team_name');

      if (teamsError) throw teamsError;
      setTeams(teamsData || []);

      // Fetch users with their profiles - FIXED: Removed 'user_id' as it doesn't exist, using 'id' instead
      const { data: usersData, error: usersError } = await supabase?.from('user_profiles')?.select('id, full_name, role')?.order('full_name');

      if (usersError) throw usersError;
      setUsers(usersData || []);
    } catch (error) {
      console.error('Error fetching teams and users:', error);
      setErrors({ fetch: error?.message });
    }
  };

  const handleRecipientToggle = (type, id, name) => {
    const recipientKey = type === 'all' ? 'all' : `${type}_${id}`;
    const recipientLabel = type === 'all' ? 'All Users' : name;

    const exists = formData?.recipients?.some(r => r?.key === recipientKey);

    if (exists) {
      setFormData({
        ...formData,
        recipients: formData?.recipients?.filter(r => r?.key !== recipientKey)
      });
    } else {
      setFormData({
        ...formData,
        recipients: [...formData?.recipients, { key: recipientKey, label: recipientLabel, type, id }]
      });
    }
  };

  const isRecipientSelected = (type, id) => {
    const recipientKey = type === 'all' ? 'all' : `${type}_${id}`;
    return formData?.recipients?.some(r => r?.key === recipientKey);
  };

  const removeRecipient = (recipientKey) => {
    setFormData({
      ...formData,
      recipients: formData?.recipients?.filter(r => r?.key !== recipientKey)
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    if (formData?.recipients?.length === 0) {
      newErrors.recipients = 'Please select at least one recipient';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const getRecipientUserIds = async () => {
    let userIds = [];

    for (const recipient of formData?.recipients) {
      if (recipient?.type === 'all') {
        // Get all users
        const { data, error } = await supabase?.from('user_profiles')?.select('id');

        if (!error && data) {
          userIds = [...userIds, ...data?.map(u => u?.id)];
        }
      } else if (recipient?.type === 'team') {
        // Get all team members - FIXED: Changed from 'id' to 'user_id' to get actual user IDs
        const { data, error } = await supabase?.from('team_members')?.select('user_id')?.eq('team_id', recipient?.id);

        if (!error && data) {
          userIds = [...userIds, ...data?.map(tm => tm?.user_id)];
        }
      } else if (recipient?.type === 'user') {
        // Individual user
        userIds?.push(recipient?.id);
      }
    }

    // Remove duplicates
    return [...new Set(userIds)];
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Clear previous errors
    setError(null);
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { data: { user: currentUser }, error: userError } = await supabase?.auth?.getUser();
      if (userError) {
        throw new Error(`Authentication error: ${userError?.message}`);
      }

      if (!currentUser?.id) {
        throw new Error('You must be logged in to send announcements');
      }

      // Get all target user IDs based on recipients
      const targetUserIds = await getRecipientUserIds();

      if (targetUserIds?.length === 0) {
        throw new Error('No valid recipients found. Please check your selection.');
      }

      console.log('Creating announcement for', targetUserIds?.length, 'recipients');

      // Create announcement in database
      const { data: announcement, error: announcementError } = await supabase
        ?.from('announcements')
        ?.insert({
          title: formData?.title?.trim(),
          message: formData?.message?.trim(),
          priority: formData?.priority?.toLowerCase(),
          created_by: currentUser?.id
        })
        ?.select()
        ?.single();

      if (announcementError) {
        console.error('Announcement creation error:', announcementError);
        throw new Error(`Failed to create announcement: ${announcementError?.message}`);
      }

      if (!announcement?.id) {
        throw new Error('Announcement was created but no ID was returned');
      }

      console.log('Announcement created with ID:', announcement?.id);

      // Get user profiles with notification emails
      const { data: userProfiles, error: profilesError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, notification_email')
        ?.in('id', targetUserIds);

      if (profilesError) {
        console.error('Failed to fetch user profiles:', profilesError);
        throw new Error(`Failed to fetch recipients: ${profilesError?.message}`);
      }

      if (!userProfiles || userProfiles?.length === 0) {
        throw new Error('No user profiles found for the selected recipients');
      }

      console.log('Creating notifications for', userProfiles?.length, 'users');

      // Create notifications in database for each recipient
      const notifications = userProfiles?.map(profile => ({
        user_id: profile?.id,
        type: 'announcement',
        title: formData?.title?.trim(),
        message: formData?.message?.trim(),
        related_type: 'announcement',
        related_id: announcement?.id,
        recipient_user_id: profile?.id,
        recipient_email: profile?.notification_email || profile?.email,
        is_read: false,
        seen: false
      }));

      const { data: createdNotifications, error: notificationsError } = await supabase
        ?.from('notifications')
        ?.insert(notifications)
        ?.select();

      if (notificationsError) {
        console.error('Notification creation error:', notificationsError);
        throw new Error(`Failed to create notifications: ${notificationsError?.message}`);
      }

      console.log('Successfully created', createdNotifications?.length, 'notifications');

      // Send email notifications via edge function with BCC privacy
      try {
        const recipientIds = userProfiles?.map(profile => profile?.id);

        const emailResponse = await fetch(
          `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              type: 'announcement',
              recipientIds: recipientIds,
              subject: `PCF Admin Announcement: ${formData?.title}`,
              data: {
                title: formData?.title,
                message: formData?.message,
                priority: formData?.priority
              }
            })
          }
        );

        if (!emailResponse?.ok) {
          const errorData = await emailResponse?.json();
          console.warn('Email notification request failed:', errorData);
        } else {
          const emailResult = await emailResponse?.json();
          console.log('Email notifications sent successfully to', emailResult?.recipientCount, 'recipients');
        }
      } catch (emailError) {
        console.warn('Email notification failed (non-critical):', emailError);
        // Don't throw - announcement and notifications were created successfully
      }

      // Reset form and show success
      setFormData({
        title: '',
        message: '',
        priority: 'normal',
        recipients: []
      });
      
      alert(`Announcement sent successfully to ${createdNotifications?.length} recipient(s)!`);
      onClose();

    } catch (err) {
      console.error('Error sending announcement:', err);
      setError(err?.message || 'Failed to send announcement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'student': 'Student',
      'city_official': 'City Official',
      'pcf_admin': 'PCF Admin',
      'business_partner': 'Business Partner',
      'mentor': 'Mentor'
    };
    return roleLabels?.[role] || role;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Send Announcement</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-micro"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Recipients Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipients <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div
                onClick={() => setShowRecipientDropdown(!showRecipientDropdown)}
                className={`w-full min-h-[44px] px-3 py-2 border ${errors?.recipients ? 'border-red-500' : 'border-gray-300'} rounded-lg cursor-pointer hover:border-blue-500 transition-micro focus-within:ring-2 focus-within:ring-blue-500`}
              >
                <div className="flex flex-wrap gap-2">
                  {formData?.recipients?.length === 0 ? (
                    <span className="text-gray-500">Select recipients...</span>
                  ) : (
                    formData?.recipients?.map((recipient) => (
                      <span
                        key={recipient?.key}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500 text-blue-100 text-sm rounded"
                      >
                        {recipient?.label}
                        <button
                          type="button"
                          onClick={(e) => {
                            e?.stopPropagation();
                            removeRecipient(recipient?.key);
                          }}
                          className="hover:bg-blue-500/20 rounded-full"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>

              {showRecipientDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                  {/* All Users Option */}
                  <div
                    onClick={() => handleRecipientToggle('all', null, 'All Users')}
                    className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-micro ${
                      isRecipientSelected('all', null) ? 'bg-gray-100' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isRecipientSelected('all', null)}
                      onChange={() => {}}
                      className="mr-3"
                    />
                    <div>
                      <p className="font-medium text-gray-900">All Users</p>
                      <p className="text-xs text-gray-500">Send to everyone in the platform</p>
                    </div>
                  </div>

                  {/* Teams Section */}
                  {teams?.length > 0 && (
                    <>
                      <div className="px-4 py-2 bg-gray-50 border-t border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Teams</p>
                      </div>
                      {teams?.map((team) => (
                        <div
                          key={team?.id}
                          onClick={() => handleRecipientToggle('team', team?.id, team?.team_name)}
                          className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-micro ${
                            isRecipientSelected('team', team?.id) ? 'bg-gray-100' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isRecipientSelected('team', team?.id)}
                            onChange={() => {}}
                            className="mr-3"
                          />
                          <div className="flex items-center gap-2">
                            <Icon name="Users" size={16} className="text-gray-500" />
                            <p className="text-gray-900">{team?.team_name}</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {/* Individual Users Section */}
                  {users?.length > 0 && (
                    <>
                      <div className="px-4 py-2 bg-gray-50 border-t border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Individual Users</p>
                      </div>
                      {users?.map((user) => (
                        <div
                          key={user?.id}
                          onClick={() => handleRecipientToggle('user', user?.id, `${user?.full_name} - ${getRoleLabel(user?.role)}`)}
                          className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-micro ${
                            isRecipientSelected('user', user?.id) ? 'bg-gray-100' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isRecipientSelected('user', user?.id)}
                            onChange={() => {}}
                            className="mr-3"
                          />
                          <div>
                            <p className="text-gray-900">{user?.full_name}</p>
                            <p className="text-xs text-gray-500">{getRoleLabel(user?.role)}</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            {errors?.recipients && (
              <p className="mt-1 text-sm text-red-500">{errors?.recipients}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Select one or multiple recipients for this announcement
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData?.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e?.target?.value });
                if (errors?.title) {
                  setErrors({ ...errors, title: null });
                }
              }}
              className={`w-full px-4 py-2 border ${errors?.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="Enter announcement title"
              disabled={loading}
            />
            {errors?.title && (
              <p className="mt-1 text-sm text-red-500">{errors?.title}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData?.message}
              onChange={(e) => {
                setFormData({ ...formData, message: e?.target?.value });
                if (errors?.message) {
                  setErrors({ ...errors, message: null });
                }
              }}
              className={`w-full px-4 py-2 border ${errors?.message ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px]`}
              placeholder="Enter announcement message (this will be displayed to users)"
              disabled={loading}
            />
            {errors?.message && (
              <p className="mt-1 text-sm text-red-500">{errors?.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              This message will be visible to all recipients in their notifications
            </p>
          </div>

          {/* Priority Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority
            </label>
            <select
              value={formData?.priority}
              onChange={(e) => setFormData({ ...formData, priority: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <Icon name="AlertCircle" size={20} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {errors?.fetch && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <Icon name="AlertTriangle" size={20} className="text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">Warning</p>
                  <p className="text-sm text-yellow-600 mt-1">{errors?.fetch}</p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
              iconName={loading ? 'Loader2' : 'Send'}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Sending...' : 'Send Announcement'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendAnnouncementModal;